import { useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertCircle,
  Calendar,
  Clock,
  Edit,
  FileText,
  Plus,
  Trash2,
  Eye,
  Award,
  CheckCircle2,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FilterBar, FilterConfig } from '@/components/ui/FilterBar'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useAuth } from '@/contexts/AuthContext'
import { useNotifications } from '@/contexts/NotificationContext'
import { useAssignments, useDeleteAssignment } from '@/hooks/useAssignments'
import { useCoursesByIds } from '@/hooks/useCourses'
import { CourseModel } from '@/models/course.model'
import { Assignment } from '@/types/assignments'
import { paths } from '@/routes/path'

type LocalFilters = {
  search?: string
  status?: string
}

const STATUS_BADGE_VARIANTS: Record<string, { label: string; classes: string }> = {
  open: { label: 'Open', classes: 'bg-green-100 text-green-700' },
  scheduled: { label: 'Scheduled', classes: 'bg-blue-100 text-blue-700' },
  closed: { label: 'Closed', classes: 'bg-gray-200 text-gray-700' },
  archived: { label: 'Archived', classes: 'bg-gray-100 text-gray-500' },
}

const statusFilterConfig: FilterConfig[] = [
  {
    key: 'search',
    type: 'search',
    label: 'Search',
    placeholder: 'Search by title or course',
  },
  {
    key: 'status',
    type: 'select',
    label: 'Status',
    options: [
      { value: '', label: 'All statuses' },
      { value: 'open', label: 'Open' },
      { value: 'scheduled', label: 'Scheduled' },
      { value: 'closed', label: 'Closed' },
      { value: 'archived', label: 'Archived' },
    ],
  },
]

export function OptimizedAssignmentsPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { addNotification } = useNotifications()
  const [filters, setFilters] = useState<LocalFilters>({})
  const [assignmentPendingDelete, setAssignmentPendingDelete] = useState<Assignment | null>(null)

  const isInstructor = user?.role === 'admin' || user?.role === 'instructor'

  const { data, isLoading, error } = useAssignments({ perPage: 100 })
  const deleteAssignment = useDeleteAssignment()

  const assignments = data?.assignments ?? []

  const courseIds = useMemo(() => assignments.map((assignment) => assignment.courseId), [assignments])
  const { data: courseLookup } = useCoursesByIds(courseIds)

  const handleFiltersChange = (values: Record<string, any>) => {
    setFilters({
      search: values.search || '',
      status: values.status || '',
    })
  }

  const filteredAssignments = useMemo(() => {
    const searchTerm = filters.search?.toLowerCase().trim()
    const status = filters.status

    return assignments.filter((assignment) => {
      const courseTitle = courseLookup?.[assignment.courseId]?.title ?? ''
      const matchesSearch =
        !searchTerm ||
        assignment.title.toLowerCase().includes(searchTerm) ||
        courseTitle.toLowerCase().includes(searchTerm)

      const matchesStatus = !status || assignment.derivedStatus === status

      return matchesSearch && matchesStatus
    })
  }, [assignments, filters, courseLookup])

  const stats = useMemo(() => {
    const total = assignments.length
    const open = assignments.filter((item) => item.derivedStatus === 'open').length
    const scheduled = assignments.filter((item) => item.derivedStatus === 'scheduled').length
    const overdue = assignments.filter(
      (item) => item.derivedStatus === 'open' && new Date(item.dueDate) < new Date()
    ).length

    return { total, open, scheduled, overdue }
  }, [assignments])

  const handleDeleteConfirmed = async () => {
    if (!assignmentPendingDelete) return
    const target = assignmentPendingDelete
    const success = await deleteAssignment.mutateAsync(target.id)
    if (success) {
      addNotification({
        title: 'Assignment deleted',
        message: `"${target.title}" has been removed.`,
        type: 'general',
        priority: 'low',
        isRead: false,
        isArchived: false,
      })
    } else {
      addNotification({
        title: 'Deletion failed',
        message: `We could not delete "${target.title}". Please try again.`,
        type: 'general',
        priority: 'high',
        isRead: false,
        isArchived: false,
      })
    }
    setAssignmentPendingDelete(null)
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Card className="max-w-md border-red-100 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              Unable to load assignments
            </CardTitle>
            <CardDescription className="text-sm text-red-600">
              Please refresh the page or try again later.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (!isInstructor) {
    return (
      <LearnerAssignmentsView
        assignments={assignments}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        courseLookup={courseLookup}
      />
    )
  }

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-3 text-sm font-medium text-red-600">
            <FileText className="h-4 w-4" />
            Instructor workspace
          </div>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">Assignments overview</h1>
          <p className="mt-2 max-w-2xl text-gray-600">
            Plan, publish, and track assignments across all of your courses. Manage due dates, attachments,
            and submissions from a single view.
          </p>
        </div>
        <Button onClick={() => navigate(paths.app.assignments.create.absolute)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Create assignment
        </Button>
      </header>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard title="Total assignments" value={stats.total} icon={<FileText className="h-4 w-4" />} />
        <StatCard title="Open for submissions" value={stats.open} icon={<CheckCircle2 className="h-4 w-4" />} tone="success" />
        <StatCard title="Scheduled / drafts" value={stats.scheduled} icon={<Calendar className="h-4 w-4" />} tone="info" />
        <StatCard title="Overdue actions" value={stats.overdue} icon={<AlertCircle className="h-4 w-4" />} tone="warning" />
      </section>

      <FilterBar filters={statusFilterConfig} onFiltersChange={handleFiltersChange} />

      <section className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {filteredAssignments.map((assignment) => {
          const course = courseLookup?.[assignment.courseId]
          return (
          <AssignmentCard
            key={assignment.id}
            assignment={assignment}
            courseTitle={course?.title}
            onView={() => navigate(paths.app.assignments.detail.build(assignment.id))}
            onEdit={() => navigate(paths.app.assignments.edit.build(assignment.id))}
            onManageSubmissions={() =>
              navigate(paths.app.assignments.submissions.list.build(assignment.id))
            }
            onDelete={() => setAssignmentPendingDelete(assignment)}
          />
        )})}
      </section>

      {filteredAssignments.length === 0 && (
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-700">
              <Award className="h-5 w-5 text-gray-500" />
              No assignments match your filters
            </CardTitle>
            <CardDescription className="text-gray-500">
              Adjust the search or status filter, or create a new assignment to get started.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      <ConfirmDialog
        open={Boolean(assignmentPendingDelete)}
        title="Delete assignment?"
        description={
          <>
            <p className="text-sm">
              The assignment{' '}
              <span className="font-semibold text-red-600">{assignmentPendingDelete?.title}</span> and all of its
              attachments will be permanently removed. This action cannot be undone.
            </p>
            <p className="text-sm text-gray-500">Learners will immediately lose access to this assignment.</p>
          </>
        }
        confirmLabel={deleteAssignment.isPending ? 'Deleting...' : 'Delete assignment'}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setAssignmentPendingDelete(null)}
        confirmTone="destructive"
      />
    </div>
  )
}

function AssignmentCard({
  assignment,
  courseTitle,
  onView,
  onEdit,
  onDelete,
  onManageSubmissions,
}: {
  assignment: Assignment
  courseTitle?: string
  onView: () => void
  onEdit: () => void
  onDelete: () => void
  onManageSubmissions?: () => void
}) {
  const statusConfig = STATUS_BADGE_VARIANTS[assignment.derivedStatus] ?? STATUS_BADGE_VARIANTS.archived
  const dueDate = new Date(assignment.dueDate)
  const isOverdue = assignment.derivedStatus === 'open' && dueDate < new Date()

  return (
    <Card className="flex h-full flex-col border border-gray-100 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <CardHeader className="space-y-3 pb-2">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="text-lg font-semibold text-gray-900">{assignment.title}</CardTitle>
            <p className="text-sm text-gray-500">
              {courseTitle ?? assignment.courseId}
            </p>
          </div>
          <Badge className={`${statusConfig.classes} capitalize`}>{statusConfig.label}</Badge>
        </div>
        <div className="flex flex-wrap gap-3 text-xs font-medium text-gray-600">
          <span className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            Due {dueDate.toLocaleString()}
          </span>
          <span className="flex items-center gap-1">
            <Award className="h-3.5 w-3.5" />
            Max points: {assignment.maxPoints}
          </span>
          {assignment.settings?.allowRetries && (
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              Retries enabled
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col justify-between space-y-4">
        <p className="line-clamp-2 text-sm text-gray-600 ">{assignment.description}</p>
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>
            Created {new Date(assignment.createdAt).toLocaleDateString()} · Last updated{' '}
            {new Date(assignment.updatedAt).toLocaleDateString()}
          </span>
          {isOverdue && (
            <span className="flex items-center gap-1 text-red-600">
              <AlertCircle className="h-3.5 w-3.5" />
              Overdue
            </span>
          )}
        </div>
      </CardContent>
      <div className="border-t border-gray-100 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Button variant="outline" size="sm" onClick={onView} className="flex items-center gap-2">
            <Eye className="h-4 w-4" />
            View
          </Button>
          <Button variant="secondary" size="sm" onClick={onEdit} className="flex items-center gap-2 text-gray-600">
            <Edit className="h-4 w-4" />
            Edit
          </Button>
          {onManageSubmissions && (
            <Button size="sm" onClick={onManageSubmissions} className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Submissions
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onDelete} className="flex items-center gap-2 text-red-600">
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
          
        </div>
        
      </div>
    </Card>
  )
}

function StatCard({
  title,
  value,
  icon,
  tone = 'default',
}: {
  title: string
  value: number
  icon: ReactNode
  tone?: 'default' | 'success' | 'info' | 'warning'
}) {
  const toneClasses: Record<string, string> = {
    default: 'bg-white',
    success: 'bg-green-50',
    info: 'bg-blue-50',
    warning: 'bg-orange-50',
  }
  return (
    <Card className={toneClasses[tone]}>
      <CardContent className="flex items-center justify-between py-6">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{title}</p>
          <p className="mt-2 text-2xl font-semibold text-gray-900">{value}</p>
        </div>
        <div className="rounded-full bg-white p-3 text-gray-600 shadow-sm">{icon}</div>
      </CardContent>
    </Card>
  )
}

function LearnerAssignmentsView({
  assignments,
  filters,
  onFiltersChange,
  courseLookup,
}: {
  assignments: Assignment[]
  filters: LocalFilters
  onFiltersChange: (values: Record<string, any>) => void
  courseLookup?: Record<string, CourseModel>
}) {
  const navigate = useNavigate()
  const filtered = useMemo(() => {
    const searchTerm = filters.search?.toLowerCase().trim()
    return assignments.filter((assignment) => {
      const courseTitle = courseLookup?.[assignment.courseId]?.title ?? ''
      const matchesSearch =
        !searchTerm ||
        assignment.title.toLowerCase().includes(searchTerm) ||
        courseTitle.toLowerCase().includes(searchTerm)
      return matchesSearch
    })
  }, [assignments, filters, courseLookup])

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-bold text-gray-900">Assignments</h1>
        <p className="mt-2 text-gray-600">
          Track due dates, download instructions, and submit your work with confidence.
        </p>
      </header>

      <FilterBar filters={statusFilterConfig.slice(0, 1)} onFiltersChange={onFiltersChange} />

      <section className="grid gap-4 md:grid-cols-2">
        {filtered.map((assignment) => (
          <Card key={assignment.id} className="border border-gray-100 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold text-gray-900">{assignment.title}</CardTitle>
                <CardDescription>{courseLookup?.[assignment.courseId]?.title ?? assignment.courseId}</CardDescription>
                </div>
                <Badge className="capitalize">
                  {STATUS_BADGE_VARIANTS[assignment.derivedStatus]?.label ?? assignment.derivedStatus}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="h-4 w-4" />
                Due {new Date(assignment.dueDate).toLocaleString()}
              </div>
              <p className="text-sm text-gray-600 line-clamp-2">{assignment.description}</p>
              <Button
                className="w-full"
                onClick={() => navigate(paths.app.assignments.detail.build(assignment.id))}
              >
                View details
              </Button>
            </CardContent>
          </Card>
        ))}
      </section>

      {filtered.length === 0 && (
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle>No assignments yet</CardTitle>
            <CardDescription>
              You will see assignments from your courses here once your instructor publishes them.
            </CardDescription>
          </CardHeader>
        </Card>
      )}
    </div>
  )
}

export default OptimizedAssignmentsPage
